import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import PrivacyContent from './PrivacyContent';
import { PRIVACY_META } from './PrivacyPage';
import { TERMS_META, TERMS_SECTIONS } from './termsContent';

/**
 * Privacy Policy / Terms shown over the flow instead of navigating away.
 *
 * A user part-way through signing shouldn't have to abandon a half-filled
 * form to read what they're agreeing to. Same text as /privacy and /terms —
 * both render the shared content modules.
 *
 * The body is wrapped in `.advi-landing` because that's where the `.legal-*`
 * rules live (features/landing/landing.css). That scope is deliberately
 * always-light and self-contained, so it renders identically in either theme.
 */

export type LegalDocument = 'privacy' | 'terms';

export default function LegalDialog({
  doc,
  onClose,
}: {
  doc: LegalDocument | null;
  onClose: () => void;
}) {
  const meta = doc === 'privacy' ? PRIVACY_META : TERMS_META;

  return (
    <Dialog open={doc !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex max-h-[86vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[760px]"
        showCloseButton
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="text-[17px] font-bold tracking-tight">{meta.title}</DialogTitle>
          <p className="text-xs text-muted-foreground">Last updated: {meta.updated}</p>
        </DialogHeader>

        <div className="advi-landing flex-1 overflow-y-auto bg-white px-6 py-5">
          <p className="legal-intro !mt-0 !text-[15px]">{meta.intro}</p>
          <div className="legal-sections !mt-6">
            {doc === 'privacy' ? (
              <PrivacyContent />
            ) : (
              TERMS_SECTIONS.map((section, i) => (
                <section className="legal-section" key={section.heading}>
                  <h2>
                    {i + 1}. {section.heading}
                  </h2>
                  {section.paragraphs.map((paragraph, j) => (
                    <p key={j}>{paragraph}</p>
                  ))}
                </section>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end border-t bg-muted/40 px-6 py-3.5">
          <Button onClick={onClose} className="cursor-pointer font-semibold">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
